/**
 * Canvas painting for the dope sheet. One entry point (`drawSheet`) that takes a plain data
 * snapshot — it never reads the store, so what it draws is exactly what the caller measured.
 *
 * The canvas cannot read CSS variables, so `PALETTE` below is the one place in the codebase that
 * repeats the design tokens as literals (`design/ui-design.md` §6, step 6). Changing a token in
 * `src/style.css` means changing its twin here. Two accents, two jobs: blue is UI state (the
 * rubber band, the selected row), orange is time and selection-in-time (playhead, selected key).
 */

import type { CanonicalBoneId, ClipEvent } from '../../shared';
import type { ClipEventRef } from '../types';
import {
  eventBandCenterY,
  frameToX,
  rowCenterY,
  rowTop,
  tickSpacing,
  visibleFrameRange,
  xToFrame,
  type Rect,
  type SheetRow,
  type TimelineMetrics,
} from './layout';
import { refKey } from './frames';

const PALETTE = {
  background: '#1d1d1d', // --sunken
  lane: '#212121',
  ruler: '#272727', // --area-2
  line: '#3d3d3d', // --edge
  rowLine: '#2a2a2a',
  text: '#e4e4e4', // --txt
  textDim: '#7e7e7e', // --txt-3
  outOfRange: '#161616',
  key: '#d6d6d6',
  keySelected: '#ed8a3c', // --orange
  summaryKey: '#8a8a8a',
  playhead: '#ed8a3c', // --orange
  selectedRow: '#2f4260',
  band: '#4772b3', // --blue
  eventBand: '#232323',
  event: '#8fa9d4',
  eventActive: '#4772b3', // --blue
};

const KEY_RADIUS = 5;
const FONT = "11px ui-monospace, 'Cascadia Mono', Consolas, monospace";

export interface SheetSnapshot {
  metrics: TimelineMetrics;
  rows: readonly SheetRow[];
  summaryFrames: readonly number[];
  /** `${bone}:${frame}` for every selected key. */
  selected: Set<string>;
  selectedBone: CanonicalBoneId | null;
  playhead: number;
  durationFrames: number;
  /** Live rubber-band rectangle, or `null`. */
  band: Rect | null;
  /** Clip events, sorted by frame — drawn in the band between the ruler and the rows. */
  events: readonly ClipEvent[];
  /** The selected marker, as a `(frame, name)` ref. */
  selectedEvent: ClipEventRef | null;
}

function diamond(c: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  c.beginPath();
  c.moveTo(x, y - r);
  c.lineTo(x + r, y);
  c.lineTo(x, y + r);
  c.lineTo(x - r, y);
  c.closePath();
}

function drawRuler(c: CanvasRenderingContext2D, s: SheetSnapshot): void {
  const m = s.metrics;
  c.fillStyle = PALETTE.ruler;
  c.fillRect(m.laneWidth, 0, Math.max(0, m.width - m.laneWidth), m.rulerHeight);

  const step = tickSpacing(m.zoom);
  const from = Math.max(0, Math.floor(xToFrame(m.laneWidth, m) / step) * step);
  const to = xToFrame(m.width, m);

  c.fillStyle = PALETTE.text;
  c.strokeStyle = PALETTE.line;
  c.textAlign = 'left';
  c.textBaseline = 'alphabetic';
  c.font = FONT;
  c.beginPath();
  for (let frame = from; frame <= to; frame += step) {
    const x = Math.round(frameToX(frame, m)) + 0.5;
    if (x < m.laneWidth) continue;
    c.moveTo(x, m.rulerHeight - 5);
    c.lineTo(x, m.rulerHeight);
    c.fillText(String(frame), x + 2, m.rulerHeight - 6);
  }
  c.stroke();
}

function drawRows(c: CanvasRenderingContext2D, s: SheetSnapshot): void {
  const m = s.metrics;
  const rowsBottom = rowTop(s.rows.length + 1, m);

  // Lane (bone name) column.
  c.fillStyle = PALETTE.lane;
  c.fillRect(0, 0, m.laneWidth, m.height);

  // Everything past the clip duration is dead space.
  const endX = frameToX(s.durationFrames, m);
  if (endX < m.width) {
    c.fillStyle = PALETTE.outOfRange;
    c.fillRect(Math.max(m.laneWidth, endX), m.rulerHeight, m.width - Math.max(m.laneWidth, endX), rowsBottom - m.rulerHeight);
  }

  c.font = FONT;
  c.textAlign = 'left';
  c.textBaseline = 'middle';

  // Summary row.
  c.fillStyle = PALETTE.text;
  c.fillText('All', 6, rowCenterY(0, m));

  s.rows.forEach((row, index) => {
    const top = rowTop(index + 1, m);
    if (row.bone === s.selectedBone) {
      c.fillStyle = PALETTE.selectedRow;
      c.fillRect(0, top, m.width, m.rowHeight);
    }
    c.fillStyle = row.frames.length > 0 ? PALETTE.text : PALETTE.textDim;
    c.fillText(row.bone, 6, rowCenterY(index + 1, m));
  });

  // Horizontal separators + the lane divider.
  c.strokeStyle = PALETTE.rowLine;
  c.beginPath();
  for (let row = 0; row <= s.rows.length + 1; row++) {
    const y = Math.round(rowTop(row, m)) + 0.5;
    c.moveTo(0, y);
    c.lineTo(m.width, y);
  }
  c.stroke();

  c.strokeStyle = PALETTE.line;
  c.beginPath();
  const laneX = Math.round(m.laneWidth) + 0.5;
  c.moveTo(laneX, 0);
  c.lineTo(laneX, m.height);
  c.stroke();
}

function drawGrid(c: CanvasRenderingContext2D, s: SheetSnapshot): void {
  const m = s.metrics;
  const step = tickSpacing(m.zoom);
  const range = visibleFrameRange(m, Number.POSITIVE_INFINITY);
  const bottom = rowTop(s.rows.length + 1, m);
  c.strokeStyle = PALETTE.rowLine;
  c.beginPath();
  for (let frame = Math.ceil(range.first / step) * step; frame <= range.last; frame += step) {
    const x = Math.round(frameToX(frame, m)) + 0.5;
    if (x < m.laneWidth) continue;
    c.moveTo(x, m.rulerHeight);
    c.lineTo(x, bottom);
  }
  c.stroke();
}

function drawKeys(c: CanvasRenderingContext2D, s: SheetSnapshot): void {
  const m = s.metrics;

  c.fillStyle = PALETTE.summaryKey;
  for (const frame of s.summaryFrames) {
    const x = frameToX(frame, m);
    if (x < m.laneWidth - KEY_RADIUS || x > m.width + KEY_RADIUS) continue;
    diamond(c, x, rowCenterY(0, m), KEY_RADIUS);
    c.fill();
  }

  s.rows.forEach((row, index) => {
    const y = rowCenterY(index + 1, m);
    for (const frame of row.frames) {
      const x = frameToX(frame, m);
      if (x < m.laneWidth - KEY_RADIUS || x > m.width + KEY_RADIUS) continue;
      const isSelected = s.selected.has(refKey(row.bone, frame));
      diamond(c, x, y, KEY_RADIUS);
      c.fillStyle = isSelected ? PALETTE.keySelected : PALETTE.key;
      c.fill();
      if (isSelected) {
        c.strokeStyle = PALETTE.keySelected;
        c.stroke();
      }
    }
  });
}

/**
 * The badge riding on top of the playhead. `FONT` is monospace, so the width comes from the digit
 * count times a fixed advance rather than from `measureText` — same result, and it does not depend
 * on a real 2D context being present.
 */
const BADGE_H = 15;
const BADGE_MIN_W = 27;
const BADGE_PAD = 6;
const BADGE_DIGIT_W = 7;

/**
 * A 1px orange line the full height of the sheet, with the current frame number in a badge on the
 * ruler (`design/ui-design.md` §4, "Timeline"). Orange is the time accent and nothing else in the
 * sheet uses it except a selected key.
 */
function drawPlayhead(c: CanvasRenderingContext2D, s: SheetSnapshot): void {
  const m = s.metrics;
  const x = Math.round(frameToX(s.playhead, m)) + 0.5;
  if (x < m.laneWidth) return;

  c.strokeStyle = PALETTE.playhead;
  c.beginPath();
  c.moveTo(x, 0);
  c.lineTo(x, m.height);
  c.stroke();

  const label = String(Math.round(s.playhead));
  c.font = FONT;
  const width = Math.max(BADGE_MIN_W, label.length * BADGE_DIGIT_W + BADGE_PAD * 2);
  // Clamped so the badge never hangs over the track-name gutter or off the right edge.
  const left = Math.min(Math.max(x - width / 2, m.laneWidth), m.width - width);

  c.fillStyle = PALETTE.playhead;
  c.fillRect(left, 0, width, BADGE_H);
  c.fillStyle = PALETTE.background;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillText(label, left + width / 2, BADGE_H / 2 + 0.5);
  c.textAlign = 'left';
  c.textBaseline = 'alphabetic';
}

function drawBand(c: CanvasRenderingContext2D, band: Rect): void {
  c.strokeStyle = PALETTE.band;
  c.strokeRect(band.x0 + 0.5, band.y0 + 0.5, band.x1 - band.x0, band.y1 - band.y0);
}

const EVENT_FLAG_W = 7;
const EVENT_FLAG_H = 8;

/**
 * The animation-event strip: one small flag per marker, with its name beside it when two markers
 * are far enough apart to leave room.
 *
 * The marker under the playhead is highlighted. That highlight is the ONLY feedback the editor
 * gives while a clip plays — the preview runs through `evaluatePose` and never touches either
 * runtime animator, so firing real handlers here would mean a second implementation of the
 * runtime's event scanner. A highlight is enough to see that a marker sits where it was meant to.
 */
function drawEventBand(c: CanvasRenderingContext2D, s: SheetSnapshot): void {
  const m = s.metrics;
  if (m.eventBandHeight <= 0) return;

  c.fillStyle = PALETTE.eventBand;
  c.fillRect(m.laneWidth, m.rulerHeight, Math.max(0, m.width - m.laneWidth), m.eventBandHeight);

  c.fillStyle = PALETTE.textDim;
  c.font = FONT;
  c.textAlign = 'left';
  c.textBaseline = 'middle';
  c.fillText('events', 6, eventBandCenterY(m));

  const centerY = eventBandCenterY(m);
  const playFrame = Math.round(s.playhead);

  for (let i = 0; i < s.events.length; i++) {
    const event = s.events[i];
    const x = Math.round(frameToX(event.frame, m)) + 0.5;
    if (x < m.laneWidth - EVENT_FLAG_W || x > m.width + EVENT_FLAG_W) continue;

    const isSelected =
      s.selectedEvent !== null &&
      s.selectedEvent.frame === event.frame &&
      s.selectedEvent.name === event.name;
    const isUnderPlayhead = event.frame === playFrame;

    const colour = isSelected
      ? PALETTE.keySelected
      : isUnderPlayhead
        ? PALETTE.eventActive
        : PALETTE.event;

    // A pennant: vertical staff with a triangle pointing right, so the marker reads as "at this
    // instant" rather than "over this span".
    c.beginPath();
    c.moveTo(x, centerY - EVENT_FLAG_H / 2);
    c.lineTo(x + EVENT_FLAG_W, centerY - EVENT_FLAG_H / 4);
    c.lineTo(x, centerY);
    c.closePath();
    c.fillStyle = colour;
    c.fill();

    c.strokeStyle = colour;
    c.beginPath();
    c.moveTo(x, centerY - EVENT_FLAG_H / 2);
    c.lineTo(x, centerY + EVENT_FLAG_H / 2);
    c.stroke();

    // The name, only when the next marker is far enough away to not collide with it.
    const next = s.events[i + 1];
    const room = next ? frameToX(next.frame, m) - x : Number.POSITIVE_INFINITY;
    if (room > 40) {
      c.fillStyle = isSelected ? PALETTE.keySelected : PALETTE.textDim;
      c.fillText(event.name, x + EVENT_FLAG_W + 3, centerY + 1);
    }
  }

  c.strokeStyle = PALETTE.line;
  c.beginPath();
  const y = Math.round(m.rulerHeight) + 0.5;
  c.moveTo(0, y);
  c.lineTo(m.width, y);
  c.stroke();
}

export function drawSheet(c: CanvasRenderingContext2D, s: SheetSnapshot): void {
  const m = s.metrics;
  c.fillStyle = PALETTE.background;
  c.fillRect(0, 0, m.width, m.height);
  drawRows(c, s);
  drawGrid(c, s);
  drawRuler(c, s);
  drawEventBand(c, s);
  drawKeys(c, s);
  drawPlayhead(c, s);
  if (s.band) drawBand(c, s.band);
}

/** Placeholder shown when the editor is not in ANIMATE mode. */
export const DISABLED_MESSAGE = 'Enter ANIMATE mode';
